import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useEffect, useState } from "react";
import { formatNumber } from "devextreme/localization";
import { calculateAge } from "@/pages/household/UP/up-utils";
import { UPRow, UniquePossibilities } from "@/types/types";
import { RADIAN } from "@/helper/globals";
import NumberBoxWrapper from "@/components/NumberBoxWrapper";
import { CURRENT_YEAR } from "@/constants/constants";
import { useAppSelector } from "@/hooks/useAppSelector";

type CustomizedLabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  index: number;
  percent: number;
};

type Props = {
  upData: UniquePossibilities;
  isFAB?: boolean;
  fabVisible?: boolean;
  initFixedIncome: number;
  setHasData: React.Dispatch<
    React.SetStateAction<{
      ab: boolean;
      fab: boolean;
    }>
  >;
};

export default function AllocationBlueprint({
  isFAB = false,
  upData,
  fabVisible,
  setHasData,
  initFixedIncome,
}: Props) {
  const { settings } = useAppSelector((state) => state.npv);
  const [equities, setEquities] = useState(0);
  const [total, setTotal] = useState(0);
  const [fixedIncome, setFixedIncome] = useState(initFixedIncome);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [pieChart, setPieChart] = useState(() => {
    return [
      {
        label: "Fixed Income",
        value: 0,
        isNeg: false,
      },
      {
        label: "Equities",
        value: 0,
        isNeg: false,
      },
    ];
  });

  useEffect(() => {
    if (upData?.data) {
      const { gridSet } = JSON.parse(upData?.data);
      if (upData?.household?.primaryContact?.birthDate) {
        const age1 = calculateAge(
          upData?.household?.primaryContact?.birthDate.toString() ?? "",
          0
        )?.value;

        if (!gridSet) return;

        const index = (gridSet as UPRow[]).findIndex((item) => item.Age.value === age1);
        const npvSettings = upData?.npvSettings
          ? JSON.parse(upData.npvSettings as string)
          : settings;

        if (isFAB) setTotal(gridSet[npvSettings.yearsToRetire + index]?.Retirement?.value ?? 0);
        else setTotal(gridSet[index]?.Retirement?.value ?? 0);

        setEquities(total - fixedIncome);
        setPieChart([
          {
            label: "Fixed Income",
            value: Math.abs(fixedIncome),
            isNeg: fixedIncome < 0 ? true : false,
          },
          {
            label: "Equities",
            value: Math.abs(total - fixedIncome),
            isNeg: total - fixedIncome < 0 ? true : false,
          },
        ]);

        if (!gridSet[index]?.Retirement?.value && !isFAB)
          setHasData((prev) => ({ ...prev, ab: false }));
        if (isFAB) setHasData((prev) => ({ ...prev, fab: false }));
      }
    }
  }, [total]);

  function renderCustomizedLabel({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
    percent,
  }: CustomizedLabelProps) {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN) - 50;
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const showLabelAndPercent = hoverIndex === index;

    return (
      <>
        <text
          className="text-2xl"
          x={x}
          y={y}
          fill="white"
          fontWeight={500}
          textAnchor={"start"}
          dominantBaseline="central"
        >
          {pieChart[index]?.label}
        </text>
        <text
          className="text-2xl"
          x={x}
          y={y + 24}
          fill="white"
          textAnchor={"start"}
          fontWeight={500}
          dominantBaseline="central"
        >
          {pieChart[index]?.isNeg ? "-" : ""}
          {formatNumber(pieChart[index]?.value ?? 0, "$ #,##0")}
        </text>
        {showLabelAndPercent && (
          <text
            className="absolute text-2xl text-white print:hidden"
            x={x + 20}
            y={y - 40}
            fill="white"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {`${(percent * 100).toFixed(2)}%`}
          </text>
        )}
      </>
    );
  }

  const fabYear = CURRENT_YEAR + settings?.yearsToRetire;
  const [isPrint, setIsPrint] = useState(false);

  useEffect(() => {
    const mediaQueryList = window.matchMedia("print");

    const handlePrint = (e: MediaQueryListEvent) => {
      setIsPrint(e.matches);
    };

    setIsPrint(mediaQueryList.matches);

    mediaQueryList.addEventListener("change", handlePrint);

    return () => {
      mediaQueryList.removeEventListener("change", handlePrint);
    };
  }, []);

  return (
    <div
      className={`relative flex-1 rounded-lg bg-neutral-500 p-8 screen:w-[calc(50%-16px)] print:mx-auto ${isFAB ? "print:break-before-page" : ""}`}
    >
      <h2 className="text-lg font-medium">
        {isFAB ? `Future Allocation Blueprint ${fabYear}` : "Allocation Blueprint"}
      </h2>
      <ResponsiveContainer
        aspect={1}
        width={fabVisible && isPrint ? "100%" : fabVisible ? "75%" : isPrint ? "100%" : "37%"}
        style={{ zoom: isPrint ? 2 : 1 }}
        className={`print relative -top-[52px] mx-auto`}
      >
        <PieChart>
          <Pie
            startAngle={-270}
            endAngle={90}
            data={pieChart}
            labelLine={false}
            label={renderCustomizedLabel}
            dataKey="value"
            onMouseEnter={(_data, index) => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            <Cell key={`cell-fixed-income-${isFAB}`} fill={"var(--primary-color)"} />
            <Cell key={`cell-equities-${isFAB}`} fill={"var(--primary-color)"} opacity={0.8} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div
        className={`absolute bottom-[34px] flex justify-between gap-7 rounded-md border border-neutral-500 bg-white p-6 shadow-light ${
          fabVisible || isPrint
            ? "w-[calc(100%-4rem)]"
            : "left-1/2 w-[calc(50%-12rem)] max-w-[805px] -translate-x-1/2"
        }`}
      >
        <NumberBoxWrapper
          format="$ #,##0"
          stylingMode={isFAB ? "filled" : "outlined"}
          label="Fixed Income"
          className="flex-1"
          width={195}
          readOnly={isFAB}
          value={fixedIncome}
          onValueChanged={(e) => {
            setFixedIncome(e.value);
            setEquities(total - e.value);
            setPieChart([
              {
                label: "Fixed Income",
                value: Math.abs(e.value),
                isNeg: e.value < 0 ? true : false,
              },
              {
                label: "Equities",
                value: total - e.value,
                isNeg: total - e.value < 0 ? true : false,
              },
            ]);
          }}
        />
        <NumberBoxWrapper
          className={`flex-1 ${
            pieChart.find((i) => i.label === "Equities")?.isNeg ? "text-danger" : ""
          }`}
          label="Equities"
          value={Math.abs(equities)}
          id="ab-equities"
          format="$ #,##0"
          width={195}
          readOnly
        />
        <NumberBoxWrapper
          className="flex-1"
          label="Total Assets"
          value={total}
          width={195}
          format="$ #,##0"
          readOnly
        />
      </div>
    </div>
  );
}
